import { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Download, Upload } from "lucide-react";

/**
 * Bulk loan / advance upload dialog. Shared between the Loans page and the
 * Payroll-run detail page so HR can file a whole batch in one click after
 * generating a payroll run (e.g. month-end advances tied to that run).
 */
export default function BulkLoanDialog({ canAutoApprove, onClose, onDone }) {
    const [downloading, setDownloading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [autoApprove, setAutoApprove] = useState(false);
    const [result, setResult] = useState(null);
    const fileRef = useRef(null);

    const onDownload = async () => {
        setDownloading(true);
        try {
            const { data } = await api.get("/bulk/loan-template", { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = "loan_template.xlsx";
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Template downloaded. Fill type + amount + tenure + reason, then upload.");
        } catch {
            toast.error("Could not download template");
        } finally {
            setDownloading(false);
        }
    };

    const onPick = () => fileRef.current?.click();
    const onUpload = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (!file.name.toLowerCase().endsWith(".xlsx")) {
            toast.error("Please upload an .xlsx file");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const qs = autoApprove ? "?auto_approve=true" : "";
            const { data } = await api.post(`/bulk/loan/import${qs}`, fd);
            setResult(data);
            toast.success(`Created ${data.created_count} loans/advances · total ₦${Number(data.total_amount).toLocaleString()}${data.auto_approved ? " (auto-approved)" : ""}`);
            onDone?.(data);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="bulk-loan-dialog">
            <div className="bg-white max-w-xl w-full rounded-lg shadow-2xl">
                <div className="px-6 py-4 border-b border-ink/10 flex items-center justify-between">
                    <h2 className="font-display font-bold text-lg flex items-center gap-2">
                        <FileText className="w-4 h-4 text-klein" /> Bulk loans &amp; advances via Excel
                    </h2>
                    <button onClick={onClose} className="text-ink/40 hover:text-ink" data-testid="bulk-loan-close">✕</button>
                </div>
                <div className="px-6 py-5 space-y-5">
                    <ol className="text-sm text-ink/70 space-y-2 list-decimal list-inside">
                        <li>Download the template — it&apos;s pre-filled with active employees + their current salary as reference.</li>
                        <li>For each employee you want to file: fill <code className="bg-bone px-1 rounded">type</code> (loan|advance), <code className="bg-bone px-1 rounded">amount</code>, <code className="bg-bone px-1 rounded">tenure_months</code> (advances always 1) and optional <code className="bg-bone px-1 rounded">reason</code>.</li>
                        <li>Leave amount blank to skip a row.</li>
                        <li>Upload — each row creates one loan/advance record. Approved entries will be deducted from the next payroll run automatically.</li>
                    </ol>

                    <div className="grid grid-cols-2 gap-3">
                        <button
                            onClick={onDownload}
                            disabled={downloading}
                            className="inline-flex items-center justify-center gap-2 border border-klein text-klein px-4 py-3 text-sm rounded hover:bg-klein hover:text-white disabled:opacity-50"
                            data-testid="bulk-loan-download-btn"
                        >
                            <Download className="w-4 h-4" />
                            {downloading ? "Downloading…" : "1. Download template"}
                        </button>
                        <button
                            onClick={onPick}
                            disabled={uploading}
                            className="inline-flex items-center justify-center gap-2 bg-klein text-white px-4 py-3 text-sm rounded hover:bg-klein/90 disabled:opacity-50"
                            data-testid="bulk-loan-upload-btn"
                        >
                            <Upload className="w-4 h-4" />
                            {uploading ? "Uploading…" : "2. Upload filled file"}
                        </button>
                        <input ref={fileRef} type="file" accept=".xlsx" hidden onChange={onUpload} data-testid="bulk-loan-file-input" />
                    </div>

                    {canAutoApprove && (
                        <label className="flex items-center gap-2 text-sm text-ink/70 cursor-pointer" data-testid="bulk-loan-auto-approve-label">
                            <input
                                type="checkbox"
                                checked={autoApprove}
                                onChange={(e) => setAutoApprove(e.target.checked)}
                                className="w-4 h-4 accent-klein"
                                data-testid="bulk-loan-auto-approve"
                            />
                            Auto-approve on creation (admin / HR only — skips the &quot;pending&quot; stage)
                        </label>
                    )}

                    {result && (
                        <div className="border border-ink/10 rounded p-4 bg-bone/40 space-y-2 text-sm" data-testid="bulk-loan-result">
                            <div className="flex justify-between"><span className="text-ink/60">Created</span><strong className="text-emerald-700">{result.created_count}</strong></div>
                            <div className="flex justify-between"><span className="text-ink/60">Skipped</span><strong>{result.skipped_count}</strong></div>
                            <div className="flex justify-between"><span className="text-ink/60">Not found</span><strong>{result.not_found_count}</strong></div>
                            <div className="flex justify-between border-t border-ink/10 pt-2"><span className="text-ink/60">Total principal</span><strong className="text-klein">₦{Number(result.total_amount).toLocaleString()}</strong></div>
                            {result.auto_approved && (
                                <div className="text-[11px] text-emerald-700 mt-1">All created entries auto-approved.</div>
                            )}
                            {result.skipped_count > 0 && result.skipped[0] && (
                                <div className="text-[11px] text-ink/50 mt-2">e.g. {result.skipped[0].reason}</div>
                            )}
                        </div>
                    )}

                    <div className="text-right pt-2">
                        <button onClick={onClose} className="px-4 py-2 text-sm text-ink/60 hover:text-ink" data-testid="bulk-loan-close-2">Close</button>
                    </div>
                </div>
            </div>
        </div>
    );
}
